// @ts-check
import { isPlatform } from '@ionic/react';
import isNull from 'lodash/isNull';
import _throttle from 'lodash/throttle';
import log from 'loglevel';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from 'Components/Base';
import Overlay from 'Components/VideoPlayer/Overlay';
import { useLearnCraftVideoPlayer } from 'Context/LearnCraftVideoPlayer';
import { useRestorePlayerFocus } from 'Util/Hooks';
import getConfig from 'Util/Config';
import {
  getPlaybackRate,
  setPlaybackRate,
  getQuality,
  setQuality,
} from 'Util/videoPreferencesStorage';
import sanitizeVideoTime from 'Util/videoProgress';

import { VIDEO_PROVIDER } from './constant';

const config = getConfig();
const { resumeVideo, completedPercentage, showUserOverlay, playbackRatesByProvider } =
  config.videoPlayer;
const { classroomTimeInterval } = config;

const UPDATE_VIDEO_FACTOR = resumeVideo.updateFactor;
const THROTTLE_UPDATE_VIDEO_TIME = 15 * UPDATE_VIDEO_FACTOR * 1000;
const RESUME_OFFSET_SEC = resumeVideo.resumeOffsetSec;
const THROTTLE_VIDEO_PROGRESS_MILLISEC = 1000;
const VIDEO_COMPLETE_PERCENTAGE = completedPercentage;
const THROTTLE_UPDATE_CLASSROOM_TIME_INTERVAL =
  classroomTimeInterval > 5000 ? classroomTimeInterval : 5000;
const CHECK_INVALID_DEVICE_INTERVAL = 1000;
// Replay always calls seek(0) first, so only treat a post-ended seek as part of a replay
// when it actually lands near the start — otherwise it's a manual seek, not a replay click.
const REPLAY_SEEK_TO_THRESHOLD_SEC = 1;
// [LAMS-2441] Mirror of the player's built-in PALY_BACK_RATE_LIST (lams: apps/player/components/constants.ts).
// Keep in sync — used only to avoid restoring a remembered rate the player wouldn't show when this BU sends no list.
const PLAYER_DEFAULT_PLAYBACK_RATES = [2, 1.75, 1.5, 1.25, 1, 0.75];

/**
 * @typedef LearnCraftBoxProps
 * @property {{url: string, videoId?: string, duration: number, provider: VIDEO_PROVIDER[keyof typeof VIDEO_PROVIDER]}} value
 * @property {string} courseId
 * @property {string} revision
 * @property {string} title
 * @property {string} type
 * @property {boolean} isResuming
 * @property {{id: string, videoTime: number}} videoProgress
 * @property {(time: number) => void} onUpdateVideoTime
 * @property {() => void} onCompleted
 * @property {(watchedDuration: number) => void} onUpdateClassroomTime
 * @property {() => void} checkInvalidDevice
 * @property {boolean} showVideoOverlayOnEnd
 * @property {RenderCustomVideoOverlayFunc} renderCustomVideoOverlay
 * @property {*} onOverlayCorrupted
 * @property {*} overlayDisplayName
 * @property {*} overlayPhoneNumber
 * @property {boolean} isLearnCraftNewLayout
 * @property {*} userProfileData
 * @property {boolean} enableLighthouseUserId
 * @property {({videoProvider, subsectionTitle, subsectionContentType, errorMessage, videoWatchedDuration, videoCompletionThreshold, videoTotalDuration}: {videoProvider: string, subsectionTitle: string, subsectionContentType: string, errorMessage: string, videoWatchedDuration: number, videoCompletionThreshold: number, videoTotalDuration: number}) => void} logSubsectionVideoOnVideoPlayerError
 *
 * @param {LearnCraftBoxProps} props
 */
const LearnCraftVideoBox = ({
  courseId,
  revision,
  value,
  title,
  type,
  isResuming,
  videoProgress,
  onUpdateVideoTime,
  onCompleted,
  onUpdateClassroomTime,
  checkInvalidDevice,
  showVideoOverlayOnEnd,
  renderCustomVideoOverlay,
  onOverlayCorrupted,
  overlayDisplayName,
  overlayPhoneNumber,
  isLearnCraftNewLayout,
  userProfileData,
  enableLighthouseUserId,
  logSubsectionVideoOnVideoPlayerError,
}) => {
  const { url: videoUrl, videoId, duration: videoDuration, provider: videoProvider } = value;
  const playerContainerRef = useRef(null);
  const isSetCompleted = useRef(false);
  const completedTime = useRef((VIDEO_COMPLETE_PERCENTAGE * videoDuration) / 100);
  const isSetResumeTime = useRef(false);
  const watchedDuration = useRef(0);
  const videoDurationRef = useRef(videoDuration);
  videoDurationRef.current = videoDuration;
  const totalWatchedDuration = useRef(0);
  const currentVideoTime = useRef(videoProgress?.videoTime ?? 0);
  const nextPlayAction = useRef('play');
  /** @type {React.MutableRefObject<'keyboard'|'mouse'|'touch_gesture'|undefined>} */
  const nextInteractionMethod = useRef(undefined);
  // A replay is a seek(0) + play() pair. The seek arrives first; stash its range here
  // so the following 'replay' log can carry it and we can skip the standalone
  // seek_forward/seek_backward log.
  /** @type {React.MutableRefObject<{seekFromSec: number, seekToSec: number}|null>} */
  const replaySeekInfoRef = useRef(null);
  const videoOrigin = useMemo(() => new URL(videoUrl).origin, []);
  const {
    activate,
    deactivate,
    playerRef,
    videoState,
    seek,
    replay,
    setPlaybackRates,
    selectPlaybackRate,
    selectQuality,
    logSubsectionVideoInteraction,
  } = useLearnCraftVideoPlayer();
  const hasRestoredVideoPreferencesRef = useRef(false);
  const restorePlayerFocus = useRestorePlayerFocus();
  const [isShowOverlay, setIsShowOverlay] = useState(false);
  const { userId, userProvider } = userProfileData;
  // Speeds for this BU; unknown/unconfigured → undefined → nothing sent → player keeps its default.
  const playbackRatesForProvider = playbackRatesByProvider?.[userProvider];

  const videoIdentity = useMemo(() => {
    if (videoId) return videoId;
    try {
      const url = new URL(videoUrl);
      return `${url.origin}${url.pathname}`;
    } catch (e) {
      return videoUrl;
    }
  }, [videoId, videoUrl]);

  const latestVideoUrlRef = useRef(videoUrl);
  latestVideoUrlRef.current = videoUrl;

  const [renderedVideoUrl, setRenderedVideoUrl] = useState(videoUrl);
  useEffect(() => {
    // Read the ref, not `videoUrl` — depending on the prop would re-run this on every re-minted
    // token, which is the navigation this is preventing.
    setRenderedVideoUrl(latestVideoUrlRef.current);
  }, [videoIdentity]);

  const previousIdentityRef = useRef(videoIdentity);
  useEffect(() => {
    const identityChanged = previousIdentityRef.current !== videoIdentity;
    previousIdentityRef.current = videoIdentity;
    if (!identityChanged && latestVideoUrlRef.current !== renderedVideoUrl) {
      log.debug(
        '[LearnCraftVideoBox] received a new video url for the same videoId — keeping the current iframe src'
      );
    }
  }, [videoUrl, videoIdentity, renderedVideoUrl]);

  const videoUrlWithMetadata = useMemo(() => {
    const url = new URL(renderedVideoUrl);
    url.searchParams.set('userId', userId);
    url.searchParams.set('courseId', courseId);
    url.searchParams.set('revision', revision);
    return url.toString();
  }, [renderedVideoUrl, userId, courseId, revision]);

  const iframeSrc = enableLighthouseUserId ? videoUrlWithMetadata : renderedVideoUrl;

  useEffect(() => {
    const latestVideoTime = videoProgress?.videoTime;
    if (
      !isSetResumeTime.current &&
      isResuming &&
      !isNull(latestVideoTime) &&
      videoState !== 'LOAD'
    ) {
      const resumeTime =
        latestVideoTime < videoDuration ? Math.max(latestVideoTime - RESUME_OFFSET_SEC, 0) : 0;

      if (resumeTime) {
        log.debug(`[LearnCraftVideoBox] resume to ${resumeTime}`);
        nextPlayAction.current = 'resume';
        if (isPlatform('android')) {
          // set Android `video.currentTime` on init video not working even through after event `canplaythrough` is fired
          // workaround: delay time before set `video.currentTime`
          new Promise(() => setTimeout(() => seek(resumeTime), 1500));
        } else {
          seek(resumeTime);
        }
      }

      isSetResumeTime.current = true;
    }
  }, [videoState, videoProgress]);

  useEffect(() => {
    if (showVideoOverlayOnEnd && videoState === 'ENDED') setIsShowOverlay(true);
  }, [videoState]);

  // [LAMS-2441] Wait until the player is ready (non-LOAD) so its listener is mounted and messages
  // aren't lost to a race, then push the BU speeds + restore the viewer's remembered speed and
  // quality. Reset per video load.
  useEffect(() => {
    if (videoState === 'LOAD') {
      hasRestoredVideoPreferencesRef.current = false;
      return;
    }
    if (hasRestoredVideoPreferencesRef.current) return;
    hasRestoredVideoPreferencesRef.current = true;

    if (Array.isArray(playbackRatesForProvider) && playbackRatesForProvider.length > 0) {
      setPlaybackRates(playbackRatesForProvider);
    }

    // [LAMS-2441] Restore only a rate that's actually in the menu the player will show — the BU list
    // if configured, else the player's built-in default — so a config change can't leave the player
    // displaying a speed that isn't selectable. Not in the menu → keep the player's 1x default.
    const allowedRates =
      Array.isArray(playbackRatesForProvider) && playbackRatesForProvider.length > 0
        ? playbackRatesForProvider
        : PLAYER_DEFAULT_PLAYBACK_RATES;
    const rememberedRate = getPlaybackRate(userId);
    if (rememberedRate && allowedRates.includes(rememberedRate)) {
      selectPlaybackRate(rememberedRate);
    }

    // [LAMS-2490] The player caps a too-high pick against the video's real renditions without
    // touching the stored value (TC03/TC05), so odah sends as-is and doesn't validate here.
    const rememberedQuality = getQuality(userId);
    if (rememberedQuality != null) {
      selectQuality(rememberedQuality);
    }
  }, [videoState]);

  const closeOverlay = useCallback(() => {
    setIsShowOverlay(false);
    restorePlayerFocus();
  }, [restorePlayerFocus]);

  // Keep ODAH modal in sync when LAMS replays on its own (e.g. Space while player focused)
  // TODO : delete this when migrate replay to player
  useEffect(() => {
    if (isShowOverlay && videoState === 'PLAY') {
      closeOverlay();
    }
  }, [videoState, isShowOverlay, closeOverlay]);

  /**
   * @param {'keyboard'|'mouse'|'touch_gesture'} [interactionMethod='mouse']
   */
  const onClickReplayInOverlay = useCallback(
    (interactionMethod = 'mouse') => {
      nextPlayAction.current = 'replay';
      nextInteractionMethod.current = interactionMethod;
      // Replay is centralized in LAMS via LearnCraftVideoPlayerProvider.replay()
      replay();
      closeOverlay();
    },
    [closeOverlay, replay]
  );

  const updateVideoTime = useCallback(
    _throttle(
      /** @param {number} currentTime  */
      (currentTime) => {
        const videoTime = sanitizeVideoTime(currentTime, {
          assetDuration: videoDurationRef.current,
        });
        if (videoTime !== null) onUpdateVideoTime(videoTime);
      },
      THROTTLE_UPDATE_VIDEO_TIME
    ),
    []
  );

  const updateClassroomTime = useCallback(
    _throttle(() => {
      const _watchedDuration = watchedDuration.current;
      if (_watchedDuration > 0) {
        onUpdateClassroomTime(_watchedDuration);
        watchedDuration.current = watchedDuration.current - _watchedDuration;
      }
    }, THROTTLE_UPDATE_CLASSROOM_TIME_INTERVAL),
    []
  );

  const handleVideoProgress = useCallback(
    _throttle(
      /** @param {number} currentTime  */
      (currentTime) => {
        watchedDuration.current++;
        totalWatchedDuration.current++;

        const shouldComplete =
          !isSetCompleted.current &&
          completedTime.current > 0 &&
          currentTime >= completedTime.current;

        if (shouldComplete) {
          onCompleted(currentTime, completedTime.current, videoDuration, videoProvider);
          isSetCompleted.current = true;
        }
      },
      THROTTLE_VIDEO_PROGRESS_MILLISEC
    ),
    []
  );

  /**
   * @param {string} action
   * @param {{
   *  currentTime?: number,
   *  seekFromSec?: number,
   *  seekToSec?: number,
   *  changeVolumeFrom?: number,
   *  changeVolumeTo?: number,
   *  speed?: number,
   *  interaction_method?: string
   * }} [info]
   */
  const logVideoInteraction = useCallback(
    (action, info = {}) => {
      const videoCurrentSec = info.currentTime ?? currentVideoTime.current;
      logSubsectionVideoInteraction({
        action,
        videoProvider,
        videoCurrentSec,
        videoThresholdSec: completedTime.current,
        videoDurationSec: videoDuration,
        videoCurrentPercent: videoDuration > 0 ? (videoCurrentSec / videoDuration) * 100 : 0,
        videoTotalWatchSec: totalWatchedDuration.current,
        seekFromSec: info.seekFromSec,
        seekToSec: info.seekToSec,
        changeVolumeFrom: info.changeVolumeFrom,
        changeVolumeTo: info.changeVolumeTo,
        speed: info.speed,
        videoCompletionStatus: isSetCompleted.current ? 'completed' : 'incomplete',
        interactionMethod: info.interaction_method,
      });
    },
    [logSubsectionVideoInteraction, videoDuration, videoProvider]
  );
  const logVideoInteractionRef = useRef(logVideoInteraction);
  logVideoInteractionRef.current = logVideoInteraction;

  const playerListener = useCallback(
    /** @param {MessageEvent<any>} message  */
    (message) => {
      if (message.origin !== videoOrigin) return;

      /** @type {IframeMessageData} */
      const { event, info } = message.data;

      // video_error may arrive without info — handle it before the info guard
      if (event === 'video_error') {
        logSubsectionVideoOnVideoPlayerError({
          videoProvider: value.provider,
          subsectionTitle: title,
          subsectionContentType: type,
          errorMessage: info?.errorMessage || info?.message || 'Unknown Error',
          videoWatchedDuration: info?.currentTime ?? 0,
          videoCompletionThreshold: completedTime.current,
          videoTotalDuration: videoDuration,
        });
        return;
      }

      if (!info) return;
      if (event === 'time_update') {
        handleVideoProgress(info.currentTime);
        updateVideoTime(info.currentTime);
        updateClassroomTime();
        currentVideoTime.current = info.currentTime;
      } else if (event === 'video_play') {
        const isReplay = nextPlayAction.current === 'replay';
        logVideoInteraction(nextPlayAction.current, {
          ...info,
          // Fold in the seek(0) range captured from the replay's seek event.
          ...(isReplay && replaySeekInfoRef.current ? replaySeekInfoRef.current : {}),
          interaction_method: nextInteractionMethod.current ?? info.interaction_method,
        });
        replaySeekInfoRef.current = null;
        nextInteractionMethod.current = undefined;
        nextPlayAction.current = 'play';
      } else if (event === 'video_pause') {
        logVideoInteraction('pause', info);
      } else if (event === 'video_seeked') {
        const seekInfo = {
          seekFromSec: info.seekFromSec ?? currentVideoTime.current,
          seekToSec: info.seekToSec ?? info.currentTime,
        };
        const isReplaySeek =
          nextPlayAction.current === 'replay' && seekInfo.seekToSec <= REPLAY_SEEK_TO_THRESHOLD_SEC;
        if (isReplaySeek) {
          replaySeekInfoRef.current = seekInfo;
        } else {
          nextPlayAction.current = 'play';
          const seekAction =
            seekInfo.seekToSec >= seekInfo.seekFromSec ? 'seek_forward' : 'seek_backward';
          logVideoInteraction(seekAction, { ...info, ...seekInfo });
        }
        currentVideoTime.current = info.currentTime;
        const seekedVideoTime = sanitizeVideoTime(info.currentTime, {
          assetDuration: videoDurationRef.current,
        });
        if (seekedVideoTime !== null) onUpdateVideoTime(seekedVideoTime);
      } else if (event === 'playback_rate_changed') {
        // [LAMS-2441] Persist the moment the viewer picks a speed so a reload keeps their choice.
        setPlaybackRate(userId, info.rate);
      } else if (event === 'quality_changed') {
        // [LAMS-2490] Every `quality_changed` is a viewer pick, so nothing to filter — the player
        // publishes it from its menu handler only (lams: VideoPlayerWithOverlay `changeQuality`;
        // our `set_quality` passes publish=false, and cap/ABR switches never publish).
        setQuality(userId, info.quality);
      } else if (event === 'volume_change') {
        logVideoInteraction('volume_change', info);
      } else if (event === 'speed_change') {
        logVideoInteraction('speed_change', info);
      } else if (event === 'video_end') {
        nextPlayAction.current = 'replay';
        currentVideoTime.current = info.currentTime;
      } else if (event === 'duration_change') {
        // FIXME: at this state missing to receive `duration_change` event
        completedTime.current = (VIDEO_COMPLETE_PERCENTAGE * info.duration) / 100;
      }
    },
    [
      handleVideoProgress,
      logSubsectionVideoOnVideoPlayerError,
      logVideoInteraction,
      onUpdateVideoTime,
      title,
      type,
      updateClassroomTime,
      updateVideoTime,
      userId,
      value.provider,
      videoDuration,
      videoOrigin,
    ]
  );

  const isVideoReady = useMemo(() => videoState !== 'LOAD', [videoState]);
  useEffect(() => {
    if (!isVideoReady) return;

    log.debug('[LearnCraftVideoBox] start listen player message');
    isSetCompleted.current = false;
    window.addEventListener('message', playerListener);
    return () => {
      log.debug('[LearnCraftVideoBox] stop listen player message');
      updateVideoTime.flush();
      handleVideoProgress.cancel();
      updateClassroomTime.cancel();
      window.removeEventListener('message', playerListener);
    };
  }, [handleVideoProgress, isVideoReady, playerListener, updateClassroomTime, updateVideoTime]);

  useEffect(() => {
    activate();
    const checkInvalidDeviceInterval = setInterval(() => {
      // this loop check invalid device (e.g. record screen) every `CHECK_INVALID_DEVICE_INTERVAL` ms
      // it should be check only when video is playing. but currently cannot check video state directly and `videoState` may wrong so always check when enter to this video.
      checkInvalidDevice();
    }, CHECK_INVALID_DEVICE_INTERVAL);
    return () => {
      clearInterval(checkInvalidDeviceInterval);
      logVideoInteractionRef.current('deactivate');
      deactivate();
    };
  }, []);

  return (
    <>
      {/* spacing bottom by footer icon height = 12 + 40 + 12 (pb + height + pt) = 64 */}
      {/* LearnCraft with online solution spacing bottom by footer icon height = 12 + 40 + 12 + 136 (pb + height + pt + heigh) = 200 follow designer */}
      <Box
        height={{
          _: `calc(100vh - ${isLearnCraftNewLayout ? '200px ' : '64px'})`,
          sm: 'calc(100vh - 64px)',
        }}
        style={{ position: 'relative' }}
        ref={playerContainerRef}
      >
        <iframe
          ref={playerRef}
          src={iframeSrc}
          title={title}
          allow="autoplay" // https://developer.chrome.com/blog/autoplay/#iframe-delegation
          sandbox="allow-scripts allow-same-origin"
          scrolling="no"
          frameBorder="0"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            minHeight: 'unset',
            minWidth: 'unset',
            border: 0,
          }}
        />
      </Box>
      <Box height="64px" bg="black" />
      {renderCustomVideoOverlay({
        isShowOverlay,
        closeOverlay,
        replay: onClickReplayInOverlay,
      })}
      {showUserOverlay && (
        <Overlay
          displayName={overlayDisplayName}
          phoneNumber={overlayPhoneNumber}
          onOverlayCorrupted={onOverlayCorrupted}
          videoRef={playerContainerRef}
        />
      )}
    </>
  );
};

LearnCraftVideoBox.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string,
  courseId: PropTypes.string,
  revision: PropTypes.string,
  value: PropTypes.shape({
    provider: PropTypes.oneOf(Object.values(VIDEO_PROVIDER)),
    videoId: PropTypes.string,
    url: PropTypes.string,
    duration: PropTypes.number,
  }),
  isResuming: PropTypes.bool,
  videoProgress: PropTypes.object,
  onUpdateVideoTime: PropTypes.func,
  onCompleted: PropTypes.func,
  onUpdateClassroomTime: PropTypes.func,
  checkInvalidDevice: PropTypes.func,
  showVideoOverlayOnEnd: PropTypes.bool,
  renderCustomVideoOverlay: PropTypes.func,
  onOverlayCorrupted: PropTypes.func,
  overlayDisplayName: PropTypes.string,
  overlayPhoneNumber: PropTypes.string,
  isLearnCraftNewLayout: PropTypes.bool,
  userProfileData: PropTypes.object,
  enableLighthouseUserId: PropTypes.bool,
  logSubsectionVideoOnVideoPlayerError: PropTypes.func,
};

LearnCraftVideoBox.defaultProps = {
  onUpdateVideoTime: () => {},
  onCompleted: () => {},
  onUpdateClassroomTime: () => {},
  checkInvalidDevice: () => {},
  enableLighthouseUserId: false,
};

export default LearnCraftVideoBox;

/**
 * @typedef {import('./Content').RenderCustomVideoOverlayFunc} RenderCustomVideoOverlayFunc
 * @typedef {import('Context/LearnCraftVideoPlayer/LearnCraftVideoPlayerProvider').IframeMessageData} IframeMessageData
 */
